import { Button, Modal, Upload, message, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PhotoEditor from 'components/PhotoEditor';
import { uploadFile } from 'api/common';
import { selectProviderChannelPhoto } from 'api/applicationForm';
import { selectProviderChannelPhoto as opsSelectProviderChannelPhoto } from 'api/operation';
import s from './s.module.less';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { FIRST_TIME_UPLOAD_PROFILE_PHOTO } from 'constants/common';
import { Email } from 'constants/contact';
import { TipIcon1, TipIcon2, TipIcon3, TipIcon4, TipIcon5 } from './components/TipIcons';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { shouldUseKlarityUi } from 'utils/common';

interface IProps {
    isNotInUniprofile?: boolean;
    mode?: 'popup' | 'inner';
    borderRadius?: boolean;
    isOps?: boolean;
    providerId: number;
    value?: string;
    title?: string;
    okText?: string;
    accept?: string;
    onChange?: (imageUrl: string) => void;
}

const PhotoEditorModal = ({
    isNotInUniprofile,
    mode = 'popup',
    borderRadius,
    isOps,
    providerId,
    value = '',
    title,
    okText = 'Save',
    accept = 'image/*',
    onChange,
}: IProps) => {
    const photoEditorRef = useRef();
    const fileInfoRef = React.useRef<UploadChangeParam<UploadFile>>();
    const localBlobUrlRef = useRef<string>('');
    const [imageSrc, setImageSrc] = useState('');
    // 裁剪器单独的图源：优先用本地 blob，避免等刚上传的远程 url 传播完成
    const [editImageSrc, setEditImageSrc] = useState('');
    // 卡片图从远程 url 加载时的 loading
    const [imgLoading, setImgLoading] = useState(false);
    const [showPhotoEditModal, setShowPhotoEditModal] = useState(false);
    const [showPhotoSelectModal, setShowPhotoSelectModal] = useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showProfilePhotoTip, setShowProfilePhotoTip] = React.useState(false);
    const [isFirstTimeUploadProfilePhoto, setIsFirstTimeUploadProfilePhoto] = React.useState(false);
    const [homeInfo] = useProviderHomeInfo();
    const showKlarityUi = shouldUseKlarityUi(homeInfo);

    useEffect(() => {
        setImageSrc(value);
        if (value) {
            setImgLoading(true);
        }
    }, [value]);

    // 生成本地 blob url 用于即时显示/裁剪，替换前先回收旧的
    const setLocalBlob = useCallback((file: Blob) => {
        if (localBlobUrlRef.current) {
            URL.revokeObjectURL(localBlobUrlRef.current);
        }
        const url = URL.createObjectURL(file);
        localBlobUrlRef.current = url;
        return url;
    }, []);

    const clearLocalBlob = useCallback(() => {
        if (localBlobUrlRef.current) {
            URL.revokeObjectURL(localBlobUrlRef.current);
            localBlobUrlRef.current = '';
        }
        setEditImageSrc('');
    }, []);

    useEffect(
        () => () => {
            if (localBlobUrlRef.current) {
                URL.revokeObjectURL(localBlobUrlRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!isNotInUniprofile && !isOps) {
            setIsFirstTimeUploadProfilePhoto(!localStorage.getItem(FIRST_TIME_UPLOAD_PROFILE_PHOTO));
        }
    }, [isNotInUniprofile]);

    const handleHideAvatarUploadModal = useCallback(() => {
        setShowPhotoSelectModal(false);
    }, []);
    const handleShowAvatarEditdModal = useCallback(() => {
        handleHideAvatarUploadModal();
        setShowPhotoEditModal(true);
    }, [handleHideAvatarUploadModal]);

    const handleShowAvatarUploadModal = useCallback(() => {
        setShowPhotoSelectModal(true);
    }, []);

    const onCancel = useCallback(() => {
        setShowPhotoEditModal(false);
        if (isFirstTimeUploadProfilePhoto) {
            setShowProfilePhotoTip(true);
        }
    }, [isFirstTimeUploadProfilePhoto]);

    const handleChange: UploadProps['onChange'] = useCallback((info: UploadChangeParam<UploadFile>) => {
        fileInfoRef.current = info;
        if (info.file.status === 'uploading') {
            setLoading(true);
        }
    }, []);

    const props: UploadProps = useMemo(
        () => ({
            name: 'file',
            multiple: false,
            onChange: handleChange,
            accept,
            showUploadList: false,
            customRequest: (data) => {
                // 选图时不上传：只生成本地 blob 作为裁剪器图源并打开裁剪器。
                // 真正的上传在点 Save 裁剪完成后进行（handleSave → handleUpload），
                // 卡片与表单值也只在裁剪保存成功后由 handleSaveAvatar 更新。
                const localUrl = setLocalBlob(data.file as Blob);
                setEditImageSrc(localUrl);
                setShowProfilePhotoTip(false);
                handleShowAvatarEditdModal();
                if (fileInfoRef.current) {
                    fileInfoRef.current.file.status = 'done';
                }
                setLoading(false);
            },
        }),
        [handleChange, accept, handleShowAvatarEditdModal, setLocalBlob],
    );

    const handleSaveAvatar = useCallback(
        async (avatarSrc: string) => {
            let res;

            if (isOps) {
                res = await opsSelectProviderChannelPhoto({
                    providerId,
                    selectedPhoto: avatarSrc || '',
                });
            } else {
                res = await selectProviderChannelPhoto({
                    photo: avatarSrc || '',
                });
            }

            if (res && !res.error) {
                // 保存成功后切回远程 url，并回收本地 blob；卡片图开始从远程加载，先显示 loading
                setImageSrc(avatarSrc);
                setImgLoading(true);
                clearLocalBlob();
                if (typeof onChange === 'function') {
                    onChange(avatarSrc);
                }
                if (!isNotInUniprofile) {
                    localStorage.setItem(FIRST_TIME_UPLOAD_PROFILE_PHOTO, 'false');
                }
                setShowPhotoEditModal(false);
                setLoading(false);
            } else {
                console.error(res);
                message.error((res && res.error) || 'Failed to save the photo, please try again.');
                setLoading(false);
            }
        },
        [isOps, onChange, providerId, clearLocalBlob, isNotInUniprofile],
    );
    const handleUpload = useCallback(
        async (file: File) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', file, `${providerId}.png`);
            const res = await uploadFile(formData);

            if (res.error) {
                setLoading(false);
                message.error(res.error);
            } else {
                // loading 交给 handleSaveAvatar，在落库完成关闭弹窗时一并复位
                handleSaveAvatar(res?.data?.data);
            }
        },
        [handleSaveAvatar, providerId],
    );
    const handleSave = useCallback(async () => {
        setLoading(true);
        try {
            const canvas = await photoEditorRef.current?.getImage();
            if (!canvas) {
                message.error('Image is not ready yet, please try again.');
                setLoading(false);
                return;
            }
            canvas.toBlob((blob: Blob | null) => {
                if (!blob) {
                    message.error('Failed to process the image, please try again.');
                    setLoading(false);
                    return;
                }
                // handleUpload 内部负责复位 loading
                handleUpload(blob);
            });
        } catch (e) {
            console.error(e);
            message.error('Failed to save the image, please try again.');
            setLoading(false);
        }
    }, [handleUpload]);

    const editContentRedner = useMemo(() => {
        return (
            <div className={s.previewBox}>
                <Spin spinning={imgLoading}>
                    <img
                        className={s.previewImage}
                        style={borderRadius ? {} : { borderRadius: 0 }}
                        src={imageSrc}
                        onLoad={() => setImgLoading(false)}
                        onError={() => setImgLoading(false)}
                    />
                </Spin>
                <div className={s.btns}>
                    <Button className={s.showEditModalBtn} onClick={handleShowAvatarEditdModal}>
                        Edit
                    </Button>
                    <Upload {...props}>
                        <Button loading={loading} icon={<UploadOutlined />}>
                            Change
                        </Button>
                    </Upload>
                </div>
            </div>
        );
    }, [borderRadius, handleShowAvatarEditdModal, imageSrc, loading, props, imgLoading]);

    const uploadButtenRender = useMemo(() => {
        return isFirstTimeUploadProfilePhoto ? (
            <Button onClick={() => setShowProfilePhotoTip(true)}>Upload</Button>
        ) : (
            <Upload {...props}>
                <Button loading={loading} icon={<UploadOutlined />}>
                    Upload
                </Button>
            </Upload>
        );
    }, [isFirstTimeUploadProfilePhoto, loading, props]);

    return (
        <div className={`${s.wrap} ${showKlarityUi ? s.wrapKlarity : ''}`}>
            <Modal
                width={800}
                title="Profile photo"
                centered
                open={showPhotoEditModal}
                okText={okText}
                onOk={handleSave}
                cancelText={isFirstTimeUploadProfilePhoto ? 'Back' : 'Cancel'}
                onCancel={onCancel}
                confirmLoading={loading}
                destroyOnClose
            >
                <PhotoEditor
                    borderRadius={borderRadius}
                    width={200}
                    height={200}
                    ref={photoEditorRef}
                    imageSrc={editImageSrc || imageSrc}
                />
            </Modal>
            <Modal
                width={800}
                open={showProfilePhotoTip}
                title="Profile photo"
                onCancel={() => setShowProfilePhotoTip(false)}
                footer={
                    <div className={s.profilePhotoTipFooterButtons}>
                        <Button onClick={() => setShowProfilePhotoTip(false)}>Cancel</Button>
                        <Upload {...props}>
                            <Button type="primary" loading={loading}>
                                Upload
                            </Button>
                        </Upload>
                    </div>
                }
                className={showKlarityUi ? s.wrapKlarity : ''}
            >
                <div className={s.profilePhotoTip}>
                    <div className={s.profilePhotoTipTitle}>
                        Upload a profile photo to make a great first impression on patients
                    </div>
                    <div className={s.profilePhotoTipList}>
                        <div className={s.tipItem}>
                            <TipIcon1 className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipTitle}>Present yourself professionally</div>
                                <div className={s.tipText}>
                                    Choose a photo where you're dressed in attire that reflects your profession and
                                    conveys a sense of competence and trustworthiness.
                                </div>
                            </div>
                        </div>
                        <div className={s.tipItem}>
                            <TipIcon2 className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipTitle}>Show your friendliness</div>
                                <div className={s.tipText}>
                                    A warm smile and friendly expression can make a big difference in helping patients
                                    feel comfortable and at ease.
                                </div>
                            </div>
                        </div>
                        <div className={s.tipItem}>
                            <TipIcon3 className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipTitle}>Use a simple background</div>
                                <div className={s.tipText}>
                                    Avoid cluttered or distracting backgrounds that take the focus away from you. A
                                    plain wall or a natural setting works well.
                                </div>
                            </div>
                        </div>
                        <div className={s.tipItem}>
                            <TipIcon4 className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipTitle}>Upload a high quality image</div>
                                <div className={s.tipText}>
                                    Make sure your photo is clear and well-lit, with no blurriness or pixelation. A
                                    high-quality image reflects professionalism and attention to detail.
                                </div>
                            </div>
                        </div>
                        <div className={s.tipItem}>
                            <TipIcon5 className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipTitle}>Need professional help?</div>
                                <div className={s.tipText}>
                                    Reach out to{' '}
                                    <a href={`mailto:${Email.klarity_provider_support}`}>
                                        {Email.klarity_provider_support}
                                    </a>{' '}
                                    if you need guidance with what images to use.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            {mode === 'popup' ? (
                <>
                    <Modal
                        width={800}
                        title={title}
                        centered
                        open={showPhotoSelectModal}
                        onCancel={handleHideAvatarUploadModal}
                        footer={null}
                    >
                        {editContentRedner}
                    </Modal>
                    <div className={s.profileAvatar}>
                        {imageSrc ? (
                            <Spin spinning={imgLoading}>
                                <img
                                    className={s.avatar}
                                    src={imageSrc}
                                    onLoad={() => setImgLoading(false)}
                                    onError={() => setImgLoading(false)}
                                />
                            </Spin>
                        ) : null}
                        {imageSrc ? (
                            <Button type="default" onClick={handleShowAvatarUploadModal}>
                                Change
                            </Button>
                        ) : (
                            uploadButtenRender
                        )}
                    </div>
                </>
            ) : (
                editContentRedner
            )}
        </div>
    );
};

export default PhotoEditorModal;
